import React from 'react';
import CellContent from '../../../../common/CellContent/CellContent';
import IndicatorBadge from '../../../../common/IndicatorBadge/IndicatorBadge';
import { convertPenniesToRub } from '../../../../../../../helpers/convertPenniesToRub';
import { requestInFlatIndicators } from '../../../../../constants/requestInFlatIndicators';
import { requestInFlatStatuses } from '../../../../../constants/common';
import {
  addressStagesColorsMap,
  requestInFlatStagesColorsMap,
} from '../../../../../constants/stagesColorMaps';
import { Checkbox, Tooltip } from 'antd';
import { columnKeyMap } from '../../constants/columnKeyMap';
import {
  TableFilterDropdown,
  TableFilterIcon,
} from '../../../../../../../components/tableComponents';
import { texts } from '../../../../../constants/texts';
import { stageIdsForAcceptanceStatuses } from '../../../../../pages/HousePage/tabs/Flats/components/TableView/config/room';
import styles from '../FlatsTable.module.scss';
import StatusTableFilter from '../../../StatusTableFilter/StatusTableFilter';

const NO_DATA_LABEL = 'Нет данных';
//! Должны быть синхронизированы с `HousePage/utils.js`
const NO_DATA_ACCEPTANCE_VALUE = '__NO_DATA_ACCEPTANCE__';
const NO_DATA_SALE_VALUE = '__NO_DATA_SALE__';

const {
  flat,
  addressStageName,
  addressStepName,
  flatAcceptanceResult,
  debt,
  immediateRequest,
  activeRequest,
  tenantCount,
  addressSaleName,
} = columnKeyMap;

const actionsColWidth = '88px';
const rowSelectionColWidth = '40px';

const getColWidth = (percent) => {
  return `calc((100% - ${actionsColWidth} - ${rowSelectionColWidth} ) * ${percent} / 100)`;
};

const hasFilters = (filters) => filters?.length > 1;

const getTextAddresses = (record) => {
  const typeNameBlock = record?.address.typeName
    ? ` ${record?.address.typeName.toLowerCase()}`
    : '';

  if (
    record?.addressLifeCycle?.stageAddressLifeCycle === 4 ||
    !record?.address?.addressDdu
  )
    return `${record?.address?.name || ''}${typeNameBlock}`;

  return (
    <span>
      {`${record?.address?.name || ''}`} (
      <Tooltip title="Номер помещения по ДДУ">
        {record?.address?.addressDdu}
      </Tooltip>
      ){`${typeNameBlock}`}
    </span>
  );
};

export const getFlatsTableColumns = ({
  expandedRowKeys,
  sortedInfo,
  filteredInfo,
  stages,
  types,
  acceptanceStatuses,
  AddAndInfoActions,
  selectedFlatsData,
  dataSource,
  onCheckAllChange,
  tableSelectionChange,
  steps,
  sales,
  setCurrentPage,
}) => {
  const hasTypes = hasFilters(types);
  const hasStages = hasFilters(stages);
  const hasSteps = hasFilters(steps);

  const flats = dataSource || [];

  const acceptanceNoDataCount = flats.filter((record) => {
    const stageId = record.addressLifeCycle?.stageAddressLifeCycle?.id;
    const acceptanceStatusName =
      record.addressLifeCycle?.acceptanceStatusAddressLifeCycle?.name;

    const hasAcceptanceData =
      stageIdsForAcceptanceStatuses.has(stageId) &&
      Boolean(acceptanceStatusName);

    return !hasAcceptanceData;
  }).length;

  const salesNoDataCount = flats.filter((record) => {
    const saleName = record.addressLifeCycle?.saleAddressLifeCycle?.name;
    return !saleName;
  }).length;

  const acceptanceStatusesWithNoData = (() => {
    const items = acceptanceStatuses ? [...acceptanceStatuses] : [];

    if (!items.some((item) => item?.value === NO_DATA_ACCEPTANCE_VALUE)) {
      items.unshift({
        value: NO_DATA_ACCEPTANCE_VALUE,
        text: (
          <StatusTableFilter
            stageCount={acceptanceNoDataCount}
            stageName={NO_DATA_LABEL}
          />
        ),
      });
    }

    return items;
  })();

  const salesWithNoData = (() => {
    const items = sales ? [...sales] : [];

    if (!items.some((item) => item?.value === NO_DATA_SALE_VALUE)) {
      items.unshift({
        value: NO_DATA_SALE_VALUE,
        text: (
          <StatusTableFilter
            stageCount={salesNoDataCount}
            stageName={NO_DATA_LABEL}
          />
        ),
      });
    }

    return items;
  })();

  const hasStatusesWithNoData = acceptanceStatusesWithNoData.length > 0;
  const hasSalesWithNoData = salesWithNoData.length > 0;

  return [
    {
      key: 'rowSelection',
      onCell: () => ({
        onClick: (e) => e.stopPropagation(),
      }),
      title: (
        <Tooltip
          title={
            selectedFlatsData.length === dataSource.length
              ? 'Снять все'
              : 'Выбрать все'
          }
          placement="topLeft"
        >
          <Checkbox
            className={styles.customCheckbox}
            indeterminate={
              !!selectedFlatsData.length &&
              selectedFlatsData.length < dataSource.length
            }
            onChange={(e) => {
              e.stopPropagation();
              onCheckAllChange(e.target.checked);
            }}
            checked={selectedFlatsData.length === dataSource.length}
          />
        </Tooltip>
      ),
      width: rowSelectionColWidth,
      render: (flatData) => {
        return (
          <Checkbox
            className={styles.customCheckbox}
            onChange={(e) => {
              e.stopPropagation();
              tableSelectionChange(flatData, e.target.checked);
            }}
            checked={selectedFlatsData?.some(
              (flat) => flat.addressId === flatData.addressId
            )}
          />
        );
      },
    },
    {
      title: '№',
      dataIndex: 'address',
      key: flat,
      colSpan: 1,
      sorter: true,
      sortOrder: sortedInfo?.columnKey === flat && sortedInfo?.order,
      filters: hasTypes ? types : [],
      filteredValue: filteredInfo?.[flat] || null,
      filterIcon: (filtered) => <TableFilterIcon filtered={filtered} />,
      filterDropdown: hasTypes
        ? (props) => {
            const { confirm } = props;

            const wrappedConfirm = () => {
              setCurrentPage(0);

              confirm();
            };

            return (
              <TableFilterDropdown
                {...props}
                confirm={wrappedConfirm}
              />
            );
          }
        : undefined,
      width: getColWidth(14),
      render: (flatData, record) => {
        const { synonym } = flatData;

        return (
          <CellContent
            isHighlighted
            topRowContent={getTextAddresses(record)}
            bottomRowContent={synonym ? `(${synonym})` : undefined}
            isSelected={expandedRowKeys.includes(record.addressId)}
            renderIndicatorBadge={
              record.hasComment
                ? ({ className }) => (
                    <Tooltip title="Есть комментарий">
                      <IndicatorBadge
                        className={className}
                        withBorder
                        bgColor={requestInFlatStagesColorsMap.get(
                          requestInFlatStatuses.comment
                        )}
                      />
                    </Tooltip>
                  )
                : undefined
            }
          />
        );
      },
    },
    {
      title: 'Стадия помещения',
      dataIndex: ['addressLifeCycle', 'stageAddressLifeCycle', 'name'],
      key: addressStageName,
      colSpan: 1,
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === addressStageName && sortedInfo?.order,
      filters: hasStages ? stages : [],
      filteredValue: filteredInfo?.[addressStageName] || null,
      filterIcon: (filtered) => <TableFilterIcon filtered={filtered} />,
      filterDropdown: hasStages
        ? (props) => {
            const { confirm } = props;

            const wrappedConfirm = () => {
              setCurrentPage(0);

              confirm();
            };

            return (
              <TableFilterDropdown
                {...props}
                confirm={wrappedConfirm}
              />
            );
          }
        : undefined,
      width: getColWidth(12),
      render: (addressLifeCycle, record) => {
        if (addressLifeCycle) {
          return (
            <CellContent
              isHighlighted
              style={{
                backgroundColor: addressStagesColorsMap.get(
                  record.addressLifeCycle.stageAddressLifeCycle.id
                ),
              }}
              topRowContent={addressLifeCycle}
            />
          );
        }
        return null;
      },
    },
    {
      title: 'Этап помещения',
      dataIndex: ['addressLifeCycle', 'stepAddressLifeCycle', 'name'],
      key: addressStepName,
      colSpan: 1,
      sorter: true,
      sortOrder: sortedInfo?.columnKey === addressStepName && sortedInfo?.order,
      filters: hasSteps ? steps : [],
      filteredValue: filteredInfo?.[addressStepName] || null,
      filterIcon: (filtered) => <TableFilterIcon filtered={filtered} />,
      filterDropdown: hasSteps
        ? (props) => {
            const { confirm } = props;

            const wrappedConfirm = () => {
              setCurrentPage(0);

              confirm();
            };

            return (
              <TableFilterDropdown
                {...props}
                confirm={wrappedConfirm}
              />
            );
          }
        : undefined,
      width: getColWidth(10),
      render: (addressLifeCycle) => {
        if (addressLifeCycle) {
          return (
            <CellContent
              isHighlighted
              topRowContent={addressLifeCycle}
            />
          );
        }
        return null;
      },
    },
    {
      title: 'Статус приемки',
      dataIndex: [
        'addressLifeCycle',
        'acceptanceStatusAddressLifeCycle',
        'name',
      ],
      key: flatAcceptanceResult,
      colSpan: 1,
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === flatAcceptanceResult && sortedInfo?.order,
      filters: hasStatusesWithNoData ? acceptanceStatusesWithNoData : [],
      filteredValue: filteredInfo?.[flatAcceptanceResult] || null,
      filterIcon: (filtered) => <TableFilterIcon filtered={filtered} />,
      onFilter: (value, record) => {
        const stageId = record.addressLifeCycle?.stageAddressLifeCycle?.id;
        const acceptanceStatusName =
          record.addressLifeCycle?.acceptanceStatusAddressLifeCycle?.name;

        const hasAcceptanceData =
          stageIdsForAcceptanceStatuses.has(stageId) &&
          Boolean(acceptanceStatusName);

        if (value === NO_DATA_ACCEPTANCE_VALUE) {
          return !hasAcceptanceData;
        }

        return hasAcceptanceData && acceptanceStatusName === value;
      },
      filterDropdown: hasStatusesWithNoData
        ? (props) => {
            const { confirm } = props;

            const wrappedConfirm = () => {
              setCurrentPage(0);

              confirm();
            };

            return (
              <TableFilterDropdown
                {...props}
                confirm={wrappedConfirm}
              />
            );
          }
        : undefined,
      width: getColWidth(13),
      render: (addressLifeCycle, record) => {
        if (
          addressLifeCycle &&
          stageIdsForAcceptanceStatuses.has(
            record.addressLifeCycle?.stageAddressLifeCycle?.id
          )
        ) {
          return (
            <CellContent
              isHighlighted
              // style={{
              //   backgroundColor: addressStagesColorsMap.get(record.stageId),
              // }}
              topRowContent={addressLifeCycle}
            />
          );
        }

        return null;
      },
    },
    {
      title: 'Продажа',
      dataIndex: ['addressLifeCycle', 'saleAddressLifeCycle', 'name'],
      key: addressSaleName,
      colSpan: 1,
      sorter: true,
      sortOrder: sortedInfo?.columnKey === addressSaleName && sortedInfo?.order,
      filters: hasSalesWithNoData ? salesWithNoData : [],
      filteredValue: filteredInfo?.[addressSaleName] || null,
      filterIcon: (filtered) => <TableFilterIcon filtered={filtered} />,
      onFilter: (value, record) => {
        const saleName = record.addressLifeCycle?.saleAddressLifeCycle?.name;

        if (value === NO_DATA_SALE_VALUE) {
          return !saleName;
        }

        return Boolean(saleName) && saleName === value;
      },
      filterDropdown: hasSalesWithNoData
        ? (props) => {
            const { confirm } = props;

            const wrappedConfirm = () => {
              setCurrentPage(0);

              confirm();
            };

            return (
              <TableFilterDropdown
                {...props}
                confirm={wrappedConfirm}
              />
            );
          }
        : undefined,
      width: getColWidth(10),
      render: (addressLifeCycle) => {
        if (addressLifeCycle) {
          return (
            <CellContent
              isHighlighted
              topRowContent={addressLifeCycle}
            />
          );
        }
        return null;
      },
    },
    {
      title: texts.debt,
      key: debt,
      colSpan: 1,
      sorter: true,
      sortOrder: sortedInfo?.columnKey === debt && sortedInfo?.order,
      width: getColWidth(9),
      render: (record) => {
        const { existDebt, amount } = record;

        if (existDebt) {
          const data = requestInFlatIndicators.find(
            (indicator) => indicator.id === requestInFlatStatuses.debt
          );

          return (
            <CellContent
              icon={<IndicatorBadge bgColor={data.bg} />}
              topRowContent={data.value}
              bottomRowContent={
                existDebt &&
                amount &&
                `${convertPenniesToRub(-1 * amount)} руб.`
              }
            />
          );
        }

        return null;
      },
    },
    {
      title: texts.immediateRequest,
      key: immediateRequest,
      colSpan: 1,
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === immediateRequest && sortedInfo?.order,
      width: getColWidth(9),
      render: (record) => {
        const { immediateRequestCount } = record;

        if (immediateRequestCount) {
          const indicatorData = requestInFlatIndicators.find(
            (indicator) => indicator.id === requestInFlatStatuses.immediate
          );

          const data = {
            ...indicatorData,
            value: `${indicatorData.value} (${immediateRequestCount})`,
          };

          return (
            <CellContent
              icon={<IndicatorBadge bgColor={data.bg} />}
              topRowContent={data.value}
            />
          );
        }

        return null;
      },
    },
    {
      title: texts.activeRequest,
      key: activeRequest,
      colSpan: 1,
      sorter: true,
      sortOrder: sortedInfo?.columnKey === activeRequest && sortedInfo?.order,
      width: getColWidth(10),
      render: (record) => {
        const { activeRequestCount } = record;

        if (activeRequestCount) {
          const indicatorData = requestInFlatIndicators.find(
            (indicator) => indicator.id === requestInFlatStatuses.active
          );

          const data = {
            ...indicatorData,
            value: `${indicatorData.value} (${activeRequestCount})`,
          };

          return (
            <CellContent
              icon={<IndicatorBadge bgColor={data.bg} />}
              topRowContent={data.value}
            />
          );
        }

        return null;
      },
    },
    {
      title: 'Кол-во жильцов',
      dataIndex: 'clients',
      key: tenantCount,
      colSpan: 1,
      sorter: true,
      sortOrder: sortedInfo?.columnKey === tenantCount && sortedInfo?.order,
      width: getColWidth(13),
      render: (clients) => <CellContent topRowContent={clients.length} />,
    },
    {
      key: 'add/info',
      colSpan: 1,
      width: actionsColWidth,
      render: (data) => (
        <AddAndInfoActions
          room={data}
          stopPropagation
          isList
        />
      ),
    },
  ];
};
